import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Button,
  Tag,
  Spin,
  message,
  Row,
  Col,
  Empty,
  Image,
  Space,
  Modal,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ShoppingOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BarcodeOutlined,
  FileTextOutlined,
  InboxOutlined,
  DownloadOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { productsApi } from '../../utils/api';
import { formatPriceToman } from '../../utils/formatPriceToman';
import { getProductImageUrl } from '../../utils/productUtils';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';
import { useEntityBreadcrumb } from '../../hooks/useEntityBreadcrumb';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import EntityBreadcrumb from '../../components/common/EntityBreadcrumb';

const formatPersianNumber = (num) => {
  if (num === null || num === undefined) return '-';
  return new Intl.NumberFormat('fa-IR').format(num);
};

const getSubCategoryName = (subCategoryId, subCategories, categories) => {
  const sub = (subCategories || []).find((s) => s.id === subCategoryId);
  if (sub?.category_name) return sub.category_name;
  const cat = (categories || []).find((c) => c.id === subCategoryId);
  return cat?.category_name || '—';
};

const ViewProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const { canEdit, canDelete } = useResourcePermissions('products');
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [taxes, setTaxes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [unitTypes, setUnitTypes] = useState([]);
  const [imagesRaw, setImagesRaw] = useState(null);
  const { trail, goToCrumb } = useEntityBreadcrumb({
    listPath: '/products',
    listLabel: 'محصولات',
    entityLabel: product?.name || (id ? `محصول #${id}` : 'محصول'),
  });

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const res = await productsApi.getById(id);
      if (res?.data) {
        setProduct(res.data.product);
        setTaxes(res.data.taxes || []);
        setCategories(res.data.categories || []);
        setSubCategories(res.data.subCategories || res.data.sub_categories || []);
        setUnitTypes(res.data.unit_types || []);
        setImagesRaw(res.data.images || null);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      message.error('خطا در دریافت اطلاعات محصول');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>حذف محصول</span>,
      content: <span style={{ fontFamily: 'CustomFont' }}>آیا از حذف این محصول اطمینان دارید؟</span>,
      okText: 'بله، حذف کن',
      cancelText: 'لغو',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await productsApi.delete(id);
          message.success('محصول با موفقیت حذف شد');
          navigate('/products');
        } catch {
          message.error('خطا در حذف محصول');
        }
      },
    });
  };

  const productTaxes = useMemo(() => {
    if (!product?.taxes) return [];
    try {
      const taxIds = typeof product.taxes === 'string' ? JSON.parse(product.taxes) : product.taxes;
      return taxes.filter((tax) => taxIds.includes(String(tax.id)));
    } catch {
      return [];
    }
  }, [product, taxes]);

  const imageUrl = useMemo(
    () => (product ? getProductImageUrl(product, imagesRaw) : null),
    [product, imagesRaw],
  );

  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category?.category_name || '—';
  };

  const getUnitTypeName = (unitId) => {
    const unit = unitTypes.find((u) => u.id === unitId);
    return unit?.unit_type || '—';
  };

  const getStatusTag = (status) => {
    const map = {
      active: { color: 'success', text: 'فعال', icon: <CheckCircleOutlined /> },
      inactive: { color: 'error', text: 'غیرفعال', icon: <CloseCircleOutlined /> },
    };
    const s = map[status] || { color: 'default', text: status, icon: null };
    return (
      <Tag color={s.color} icon={s.icon} className="text-sm px-3 py-0.5">
        {s.text}
      </Tag>
    );
  };

  const getTypeLabel = (type) => ({ goods: 'کالا', service: 'خدمات' }[type] || type);

  const DetailField = ({ label, children, fullWidth = false }) => (
    <div className={fullWidth ? 'col-span-full' : ''}>
      <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</div>
      <div className="text-gray-900 dark:text-gray-100 break-words">{children}</div>
    </div>
  );

  if (loading) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  if (!product) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center h-64">
          <Empty description="محصول یافت نشد" />
        </div>
      </MainLayout>
    );
  }

  const downloadUrl = product.download_file_url || null;
  const downloadName = product.downloadable_file || 'فایل محصول';

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <EntityBreadcrumb trail={trail} onNavigate={goToCrumb} />
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center flex-shrink-0">
                <ShoppingOutlined className="text-purple-600 dark:text-purple-400 text-xl" />
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0 truncate">
                  {product.name}
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {getStatusTag(product.status)}
                  <Tag color="blue">{getTypeLabel(product.type)}</Tag>
                </div>
              </div>
            </div>
            <Space wrap className="flex-shrink-0">
              {canEdit ? (
                <Button icon={<EditOutlined />} onClick={() => navigate(`/products/${id}/edit`)}>
                  ویرایش
                </Button>
              ) : null}
              {canDelete ? (
                <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                  حذف
                </Button>
              ) : null}
              <Button
                icon={<ArrowRightOutlined />}
                onClick={() => navigate(returnToRef.current || '/products', { state: buildGoBackState() })}
              >
                بازگشت
              </Button>
            </Space>
          </div>

          <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Row gutter={[16, 16]}>
              {/* Sidebar: image + download */}
              <Col xs={24} lg={8} className="flex flex-col gap-4">
                <Card
                  title={
                    <span className="flex items-center gap-2">
                      <PictureOutlined />
                      تصویر محصول
                    </span>
                  }
                  className="dark:bg-gray-800 border-0 shadow-sm"
                  styles={{ body: { padding: imageUrl ? 12 : 24 } }}
                >
                  {imageUrl ? (
                    <div className="flex justify-center">
                      <Image
                        src={imageUrl}
                        alt={product.name}
                        className="rounded-lg object-contain max-h-72"
                        style={{ maxWidth: '100%' }}
                      />
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="تصویری آپلود نشده است" />
                  )}
                </Card>

                {product.downloadable ? (
                  <Card
                    title={
                      <span className="flex items-center gap-2">
                        <DownloadOutlined />
                        فایل قابل دانلود
                      </span>
                    }
                    className="dark:bg-gray-800 border-0 shadow-sm"
                  >
                    {downloadUrl ? (
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        <div className="flex-1 min-w-0 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
                          <p className="text-sm text-gray-600 dark:text-gray-300 m-0 mb-1">نام فایل</p>
                          <p className="font-medium text-gray-800 dark:text-gray-100 m-0 truncate" title={downloadName}>
                            {downloadName}
                          </p>
                        </div>
                        <Button
                          type="primary"
                          icon={<DownloadOutlined />}
                          href={downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-blue-600 border-blue-600 flex-shrink-0"
                        >
                          دانلود فایل
                        </Button>
                      </div>
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="فایلی برای دانلود ثبت نشده" />
                    )}
                  </Card>
                ) : null}
              </Col>

              {/* Main info */}
              <Col xs={24} lg={16}>
                <Card className="dark:bg-gray-800 border-0 shadow-sm h-full" styles={{ body: { fontFamily: 'CustomFont' } }}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                    <DetailField label="قیمت فروش">
                      <span className="text-blue-600 font-bold text-lg">
                        {formatPriceToman(product.price)}
                      </span>
                    </DetailField>
                    {product.purchase_price ? (
                      <DetailField label="قیمت خرید">
                        {formatPriceToman(product.purchase_price)}
                      </DetailField>
                    ) : null}
                    <DetailField label="SKU">
                      <Space>
                        <BarcodeOutlined />
                        {product.sku || '—'}
                      </Space>
                    </DetailField>
                    <DetailField label="واحد">
                      {getUnitTypeName(product.unit_id)}
                    </DetailField>
                    <DetailField label="دسته‌بندی">
                      {getCategoryName(product.category_id)}
                    </DetailField>
                    <DetailField label="زیردسته‌بندی">
                      {product.sub_category_id
                        ? getSubCategoryName(product.sub_category_id, subCategories, categories)
                        : '—'}
                    </DetailField>
                    {productTaxes.length > 0 ? (
                      <DetailField label="مالیات‌ها" fullWidth>
                        <Space wrap>
                          {productTaxes.map((tax) => (
                            <Tag key={tax.id} color="orange">
                              {tax.tax_name} ({tax.rate_percent}%)
                            </Tag>
                          ))}
                        </Space>
                      </DetailField>
                    ) : null}
                    <DetailField label="اجازه خرید">
                      <Tag color={product.allow_purchase ? 'success' : 'default'}>
                        {product.allow_purchase ? 'بله' : 'خیر'}
                      </Tag>
                    </DetailField>
                    <DetailField label="قابل دانلود">
                      <Tag color={product.downloadable ? 'success' : 'default'}>
                        {product.downloadable ? 'بله' : 'خیر'}
                      </Tag>
                    </DetailField>
                    {product.track_inventory === '1' ? (
                      <>
                        <DetailField label="پیگیری موجودی">
                          <Tag color="success">فعال</Tag>
                        </DetailField>
                        {product.opening_stock != null ? (
                          <DetailField label="موجودی اولیه">
                            <Space>
                              <InboxOutlined />
                              {formatPersianNumber(product.opening_stock)}
                            </Space>
                          </DetailField>
                        ) : null}
                      </>
                    ) : null}
                    {product.description ? (
                      <DetailField label="توضیحات" fullWidth>
                        <div className="flex items-start gap-2">
                          <FileTextOutlined className="mt-1 flex-shrink-0" />
                          <span className="whitespace-pre-wrap">{product.description}</span>
                        </div>
                      </DetailField>
                    ) : null}
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ViewProduct;
